import { useMemo, useState } from "react";
import { X, Gift, Star, Key, Coffee, ForkKnife, Hoodie, Smiley, TShirt, Ticket } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const SEG_COLORS = ["#4A235A", "#7E5B94", "#B7791F", "#2F6B4F", "#8A5A44", "#5B7E9B", "#96506B", "#6B6B3A", "#37455C"];

const prizeIcon = (label) => {
  const l = String(label || "").toLowerCase();
  if (l.includes("hoodie")) return Hoodie;
  if (l.includes("keychain") || l.includes("key chain")) return Key;
  if (l.includes("mug") || l.includes("coffee")) return Coffee;
  if (l.includes("tee") || l.includes("t-shirt")) return TShirt;
  if (l.includes("lunch") || l.includes("meal")) return ForkKnife;
  if (l.includes("gift") || l.includes("card") || l.includes("gas")) return Ticket;
  if (l.includes("luck") || l.includes("no prize")) return Smiley;
  if (l.includes("point")) return Star;
  return Gift;
};

export function WheelModal({ prizes, onDone }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [claimed, setClaimed] = useState("");

  const gradient = useMemo(() => {
    const n = prizes.length;
    const step = 360 / n;
    const parts = prizes.map((_, i) => {
      const col = SEG_COLORS[i % SEG_COLORS.length];
      return `${col} ${(i * step).toFixed(2)}deg ${((i + 1) * step).toFixed(2)}deg`;
    });
    return `conic-gradient(${parts.join(", ")})`;
  }, [prizes]);

  const spin = async () => {
    if (spinning || result) return;
    setSpinning(true);
    try {
      const { data } = await api.post("/shift-notes/rewards/spin");
      const n = prizes.length;
      const step = 360 / n;
      const target = 360 * 6 + (360 - (data.index * step + step / 2));
      setRotation(target);
      setTimeout(() => {
        setResult(data);
        setSpinning(false);
      }, 4200);
    } catch (e) {
      toast.error(formatApiError(e));
      setSpinning(false);
      onDone();
    }
  };

  const claim = async (choice) => {
    try {
      await api.post("/shift-notes/rewards/claim", { win_id: result.win_id, choice });
      setClaimed(choice);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wheel-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 text-center shadow-xl sm:p-8">
        <div className="flex items-start justify-between">
          <p className="font-heading text-2xl tracking-tight text-[#4A235A]">Wrap-up wheel</p>
          <button onClick={onDone} className="text-stone-400 hover:text-stone-700" data-testid="wheel-close"><X size={20} /></button>
        </div>
        <p className="mt-1 text-xs text-stone-500">You earned this spin with your wrap-ups. Good luck!</p>

        <div className="relative mx-auto mt-5 h-72 w-72">
          <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1">
            <div className="h-0 w-0 border-x-[12px] border-t-[20px] border-x-transparent border-t-stone-800" />
          </div>
          <div className="h-full w-full rounded-full border-4 border-stone-800 shadow-lg"
            style={{
              background: gradient,
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)" : "none",
            }}
            data-testid="wheel-disc">
            {prizes.map((p, i) => {
              const step = 360 / prizes.length;
              const angle = step * i + step / 2;
              const Icon = prizeIcon(p);
              return (
                <div key={i} className="pointer-events-none absolute inset-0 flex items-start justify-center"
                  style={{ transform: `rotate(${angle}deg)` }}>
                  <Icon size={30} weight="fill" className="mt-8 text-white drop-shadow-md" />
                </div>
              );
            })}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-stone-200 bg-white shadow-md">
              <Gift size={26} className="text-[#4A235A]" />
            </div>
          </div>
        </div>

        <div className="mx-auto mt-4 grid max-w-sm grid-cols-2 gap-x-4 gap-y-1.5 text-left" data-testid="wheel-legend">
          {prizes.map((p, i) => {
            const Icon = prizeIcon(p);
            return (
              <p key={i} className="flex items-center gap-1.5 text-xs text-stone-600" data-testid={`wheel-legend-${i}`}>
                <Icon size={14} weight="fill" className="shrink-0 text-[#4A235A]" />
                <span className="truncate">{p}</span>
              </p>
            );
          })}
        </div>

        {result ? (
          <div className="mt-5" data-testid="wheel-result">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">
              {result.kind === "none" ? "No prize this time" : "You won"}
            </p>
            <p className="mt-1 font-heading text-3xl tracking-tight text-[#4A235A]" data-testid="wheel-result-prize">{result.prize}</p>
            {result.value && <p className="mt-0.5 text-xs font-semibold text-[#2F6B4F]">a {result.value} value</p>}
            {result.kind === "points" && (
              <p className="mt-2 text-xs text-stone-500">{result.points} points added to your Swag Shop balance.</p>
            )}
            {result.kind === "none" && (
              <p className="mt-2 text-xs text-stone-500">Still {result.points || 1} point for showing up — wrap up tomorrow and spin again.</p>
            )}
            {result.kind === "giftcard" && (
              <p className="mt-2 text-xs text-stone-500">The office releases your gift card after a quick approval — it will appear in your Swag Shop.</p>
            )}
            {result.kind === "swag" && !result.needs_choice && (
              <p className="mt-2 text-xs text-stone-500">Pick it up during office pickup windows, or we'll bring it to you — the office will reach out.</p>
            )}
            {result.needs_choice && !claimed && (
              <div className="mt-3 space-y-2" data-testid="wheel-choice">
                <p className="text-xs font-medium text-stone-600">Your choice:</p>
                <button onClick={() => claim("item")}
                  className="block w-full rounded-full bg-[#4A235A] px-5 py-2 text-sm font-semibold text-white hover:bg-stone-900"
                  data-testid="wheel-claim-item">
                  Take the {result.prize}{result.value ? ` (${result.value} value)` : ""}
                </button>
                <button onClick={() => claim("points")}
                  className="block w-full rounded-full border border-stone-300 px-5 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-50"
                  data-testid="wheel-claim-points">
                  Take {result.alt_points} points instead
                </button>
              </div>
            )}
            {claimed && (
              <p className="mt-2 text-xs font-medium text-[#2F6B4F]" data-testid="wheel-claimed">
                {claimed === "points" ? `${result.alt_points} points added to your balance.` : "It's yours — the office will arrange pickup or delivery."}
              </p>
            )}
            <button onClick={onDone}
              className="mt-4 rounded-full bg-[#4A235A] px-6 py-2 text-sm font-semibold text-white hover:bg-stone-900"
              data-testid="wheel-done">Done</button>
          </div>
        ) : (
          <button onClick={spin} disabled={spinning}
            className="mt-5 rounded-full bg-[#4A235A] px-8 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-105 disabled:opacity-50"
            data-testid="wheel-spin-btn">
            {spinning ? "Spinning…" : "Spin!"}
          </button>
        )}
      </div>
    </div>
  );
}
